import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { Icon } from "@cntrl/ui";
import releaseShot from "./assets/tour-release.jpg";
import homeShot from "./assets/tour-home.jpg";
import dosShot from "./assets/tour-dos.jpg";
import socialsShot from "./assets/tour-socials.jpg";
import { setStatusBarStyle } from "@cntrl/native";
import { openApplyOnline } from "./applyOnline.js";
import styles from "./TourScreen.module.scss";

interface TourStep {
  title: string;
  body: string;
  shot: string;
  shotAlt: string;
  chipBadge: string;
  chipHead: string;
  chipSub?: string;
}

/** The four steps (ART-346). The handoff's fifth, Pricing, is gone: the app shows no prices. */
const STEPS: readonly TourStep[] = [
  {
    title: "Push and promote your next release.",
    body: "Keep your masters, Atmos files, artwork, credits and the timeline organized across your camp. Share one page with distro and press.",
    shot: releaseShot,
    shotAlt: "The Artist CNTRL release page",
    chipBadge: "LNK",
    chipHead: "Smart link is live",
    chipSub: "OUT FRIDAY · 12 DSPS",
  },
  {
    title: "Manage your booking and financials.",
    body: "Plan out each show or tour with your band members and team. Set up financial splits, send contracts, and more.",
    shot: homeShot,
    shotAlt: "The Artist CNTRL home screen",
    chipBadge: "MO",
    chipHead: "Invite sent to Maya Ortiz, tour manager",
  },
  {
    title: "Take it on the road.",
    body: "Complete mobile app with added features for travel and day-of-show notifications, plus group messaging so everyone stays on the same page.",
    shot: dosShot,
    shotAlt: "The Artist CNTRL day of show screen",
    chipBadge: "✈",
    chipHead: "Maya's flight to BNA is delayed 25 min",
    chipSub: "FLIGHTS · NOW",
  },
  {
    title: "See what's hitting in real-time.",
    body: "Get breakout alerts and advanced metrics to stay on top of how your content is performing across all of your platforms.",
    shot: socialsShot,
    shotAlt: "The Artist CNTRL socials screen",
    chipBadge: "↑",
    chipHead: "Spotify streams jumped +165%",
    chipSub: "SPOTIFY · YESTERDAY",
  },
];

export const TOUR_STEPS = STEPS.length;

/** How far a drag must travel, as a share of the page width, to move one page on release. */
const SWIPE_DISTANCE = 0.25;
/** …or how fast a flick must be, in px per ms, whatever its distance. */
const SWIPE_VELOCITY = 0.35;

/**
 * The iOS app's "Learn more" tour (ART-346): four steps, Releases · Booking · On the road · Real-time. The pages SWIPE
 * while the top bar and the foot (dots, Next, Apply online) stay put. Close (step 1) or Back, and Skip, lead out; every
 * step carries **Apply online**, which on the last step is the only way forward.
 *
 * The swipe is a hand-rolled drag on a translated track rather than CSS scroll-snap. Scroll-snap in the web view could
 * stop between two pages, and reading the page back off the scroll position fought Next's own animated scroll. Here
 * the current page is plain state: the track follows the finger and, on release, ALWAYS settles on a page. Next and
 * Back move the same track, so they play the same slide.
 */
export function TourScreen({
  step,
  onStep,
  onExit,
}: {
  /** 1-based; where the tour opens. */
  step: number;
  /** The step the tour settled on, 1-based. */
  onStep: (next: number) => void;
  onExit: () => void;
}) {
  const [i, setI] = useState(() => Math.min(Math.max(step, 1), STEPS.length) - 1);
  const [dragX, setDragX] = useState<number | null>(null);
  const drag = useRef<{ x: number; y: number; t: number; id: number; horizontal: boolean | null } | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const last = i === STEPS.length - 1;

  // These screens sit outside the mobile shell, which is what normally sets the status bar, so they set their own:
  // dark content, because the tour is a light page.
  useEffect(() => setStatusBarStyle("dark"), []);

  // Report the settled page (the route keeps it in the URL). After the render, never during it.
  const reported = useRef(i);
  useEffect(() => {
    if (reported.current !== i) {
      reported.current = i;
      onStep(i + 1);
    }
  }, [i, onStep]);

  const go = (to: number) => setI(Math.min(Math.max(to, 0), STEPS.length - 1));

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX, y: e.clientY, t: performance.now(), id: e.pointerId, horizontal: null };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    // Decide once, on the first real movement, whether this is a sideways swipe; a vertical one is left alone.
    if (d.horizontal === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      d.horizontal = Math.abs(dx) > Math.abs(dy);
      if (d.horizontal) (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
    if (!d.horizontal) return;
    // Past the first or last page the track resists rather than sliding off into nothing.
    const atEdge = (dx > 0 && i === 0) || (dx < 0 && last);
    setDragX(atEdge ? dx / 3 : dx);
  };

  const onPointerEnd = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.id !== e.pointerId || !d.horizontal) {
      setDragX(null);
      return;
    }
    const dx = e.clientX - d.x;
    const width = viewportRef.current?.clientWidth || 1;
    const velocity = dx / Math.max(performance.now() - d.t, 1);
    if (dx < -width * SWIPE_DISTANCE || velocity < -SWIPE_VELOCITY) go(i + 1);
    else if (dx > width * SWIPE_DISTANCE || velocity > SWIPE_VELOCITY) go(i - 1);
    setDragX(null);
  };

  return (
    <main className={styles.screen}>
      <div className={styles.top}>
        {i === 0 ? (
          <button type="button" className={styles.round} aria-label="Close" onClick={onExit}>
            <Icon icon={X} size={20} />
          </button>
        ) : (
          <button type="button" className={styles.round} aria-label="Back" onClick={() => go(i - 1)}>
            <Icon icon={ArrowLeft} size={20} />
          </button>
        )}
        <button type="button" className={styles.skip} onClick={onExit}>
          Skip
        </button>
      </div>

      <div
        ref={viewportRef}
        className={styles.viewport}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <div
          className={styles.track}
          data-dragging={dragX !== null || undefined}
          style={{ transform: `translateX(calc(${-i * 100}% + ${dragX ?? 0}px))` }}
        >
          {STEPS.map((s, n) => (
            // Off-screen pages are hidden from assistive tech: a screen reader reads the page that is showing.
            <section key={n} className={styles.page} aria-hidden={n !== i || undefined}>
              <div className={styles.card}>
                <div aria-hidden="true" className={styles.glow} />
                <div className={styles.phone}>
                  <img src={s.shot} alt={s.shotAlt} className={styles.shot} draggable={false} />
                </div>
                <div className={styles.chip}>
                  <span className={styles.chipBadge} aria-hidden="true">{s.chipBadge}</span>
                  <span>
                    <span className={styles.chipHead}>{s.chipHead}</span>
                    {s.chipSub && <span className={styles.chipSub}>{s.chipSub}</span>}
                  </span>
                </div>
              </div>

              <div className={styles.num}>{String(n + 1).padStart(2, "0")}</div>
              <h1 className={styles.title}>{s.title}</h1>
              <p className={styles.body}>{s.body}</p>
            </section>
          ))}
        </div>
      </div>

      <div className={styles.foot}>
        <div className={styles.navRow}>
          <div className={styles.dots} aria-label={`Step ${i + 1} of ${STEPS.length}`} role="img">
            {STEPS.map((_, n) => (
              <span key={n} className={styles.dot} data-active={n === i || undefined} />
            ))}
          </div>
          {!last && (
            <button type="button" className={styles.next} onClick={() => go(i + 1)}>
              Next <Icon icon={ArrowRight} size={16} />
            </button>
          )}
        </div>
        <button type="button" className={styles.apply} onClick={() => void openApplyOnline()}>
          Apply online
        </button>
      </div>
    </main>
  );
}
